import { currentStage } from "@services/env";
import { Alert, Space, Tag } from "antd";
import { useEffect, useState } from "react";

/**
 * BetaBanner displays a warning banner in non-production environments
 * to inform users about potential data delays.
 *
 * To remove this banner:
 * 1. Delete this file (BetaBanner.tsx)
 * 2. Remove the import and usage from views/index.tsx
 */
const BETA_BANNER_DISMISSED_KEY = "betaBannerDismissedDate";

/**
 * Get today's date as a string in YYYY-MM-DD format
 */
function getTodayDateString(): string {
  return new Date().toISOString().split("T")[0];
}

/**
 * Check if the banner was already dismissed today
 */
function wasDismissedToday(): boolean {
  const dismissedDate = localStorage.getItem(BETA_BANNER_DISMISSED_KEY);
  return dismissedDate === getTodayDateString();
}

function BetaBanner() {
  const [visible, setVisible] = useState(false);
  const isNonProdEnvironment = currentStage !== "Prod";

  useEffect(() => {
    // Only show the banner if it wasn't dismissed today
    if (isNonProdEnvironment && !wasDismissedToday()) {
      setVisible(true);
    }
  }, [isNonProdEnvironment]);

  useEffect(() => {
    // Auto-dismiss the banner after 1 minute
    if (!visible) {
      return;
    }

    const timer = setTimeout(() => {
      localStorage.setItem(BETA_BANNER_DISMISSED_KEY, getTodayDateString());
      setVisible(false);
    }, 20000);

    return () => clearTimeout(timer);
  }, [visible]);

  const handleClose = () => {
    // Store today's date when user dismisses the banner
    localStorage.setItem(BETA_BANNER_DISMISSED_KEY, getTodayDateString());
    setVisible(false);
  };

  if (!isNonProdEnvironment || !visible) {
    return null;
  }

  return (
    <Alert
      banner
      type="warning"
      showIcon={false}
      // icon={<ClockCircleOutlined />}
      message={
        <Space>
          <Tag color="orange">{currentStage}</Tag>
          <span>Data is synced daily from production. Not real-time with production changes.</span>
        </Space>
      }
      closable
      onClose={handleClose}
      style={{ padding: "0.5rem 2rem" }}
    />
  );
}

export default BetaBanner;
